/* eslint-disable react/prop-types */
import React, { useState, useRef, useEffect } from 'react';
import {
  addMonths,
  endOfMonth,
  format,
  parseISO,
  startOfMonth
} from 'date-fns';
import { DateRangePicker, createStaticRanges } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { addToQueryString } from '../../utils';
import { addYears, startOfYear, endOfYear } from 'date-fns';
import { Button, Input } from '../ui';

const DatePicker = ({ dateRange, onChange, direction, className = '' }) => {
  const pickerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  const [selectionRange, setSelectionRange] = useState({
    startDate: dateRange?.startDate
      ? parseISO(dateRange?.startDate)
      : new Date(),
    endDate: dateRange?.endDate ? parseISO(dateRange?.endDate) : new Date(),
    key: 'selection'
  });

  const [showCalendar, setShowCalendar] = useState(false);

  const handleSelect = ranges => {
    const { selection } = ranges;
    setSelectionRange(selection);
  };

  const handleApply = () => {
    const { startDate, endDate } = selectionRange;

    addToQueryString(format(startDate, 'yyyy-MM-dd'), 'startDate');
    addToQueryString(format(endDate, 'yyyy-MM-dd'), 'endDate');

    onChange({
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd')
    });

    setShowCalendar(false);
  };

  const handleCancel = () => {
    setShowCalendar(false);
  };

  const handleClickOutside = event => {
    if (pickerRef.current && !pickerRef.current.contains(event.target)) {
      setShowCalendar(false);
    }
  };

  useEffect(() => {
    if (showCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendar]);

  useEffect(() => {
    if (dateRange) {
      setSelectionRange({
        startDate: dateRange?.startDate
          ? parseISO(dateRange?.startDate)
          : new Date(),
        endDate: dateRange?.endDate ? parseISO(dateRange?.endDate) : new Date(),
        key: 'selection'
      });
    }
  }, [dateRange]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const customStaticRanges = [
    {
      label: 'Today',
      range: () => {
        const today = new Date();
        return {
          startDate: today,
          endDate: today
        };
      }
    },
    {
      label: 'This Month',
      range: () => ({
        startDate: startOfMonth(new Date()),
        endDate: endOfMonth(new Date())
      })
    },
    {
      label: 'Last Month',
      range: () => ({
        startDate: startOfMonth(addMonths(new Date(), -1)),
        endDate: endOfMonth(addMonths(new Date(), -1))
      })
    },
    {
      label: 'Last 3 Months',
      range: () => {
        const now = new Date();
        return {
          startDate: startOfMonth(addMonths(now, -2)),
          endDate: endOfMonth(now)
        };
      }
    },
    {
      label: 'Last 6 Months',
      range: () => {
        const now = new Date();
        return {
          startDate: startOfMonth(addMonths(now, -5)),
          endDate: endOfMonth(now)
        };
      }
    },
    {
      label: 'This Year',
      range: () => ({
        startDate: startOfYear(new Date()),
        endDate: endOfYear(new Date())
      })
    },
    {
      label: 'Last Year',
      range: () => ({
        startDate: startOfYear(addYears(new Date(), -1)),
        endDate: endOfYear(addYears(new Date(), -1))
      })
    },
    {
      label: 'Last 2 Years',
      range: () => ({
        startDate: startOfYear(addYears(new Date(), -2)),
        endDate: endOfYear(new Date())
      })
    }
  ];

  const staticRanges = createStaticRanges([...customStaticRanges]);
  const pickerLayoutDirection = isMobile ? 'vertical' : 'horizontal';
  const isPopupUp = direction === 'up';

  return (
    <>
      <div
        ref={pickerRef}
        className={`relative inline-block w-full align-top sm:w-59 ${className}`.trim()}
      >
        <div className="relative w-full sm:w-59">
          <span className="absolute left-3 top-2.5">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="18"
              height="18"
            >
              <path d="M19 3h-2V2h-2v1H9V2H7v1H5c-1.1 0-1.99.9-1.99 2L3 19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 16H8v-2h4v2zm6-4H5V8h14v7z" />
            </svg>
          </span>
          <Input
            className="h-10 pl-9.5 pr-2"
            type="text"
            value={`${format(selectionRange?.startDate, 'MMM d, yyyy')} - ${format(selectionRange?.endDate, 'MMM d, yyyy')}`}
            onClick={() => setShowCalendar(!showCalendar)}
            readOnly
          />
        </div>

        {showCalendar && (
          <div
            className={`date-picker-popover absolute left-1/2 top-full z-40 mt-2 max-h-[calc(100vh-2rem)] w-[calc(100vw-1rem)] max-w-[720px] -translate-x-1/2 overflow-auto rounded-lg border border-border bg-surface p-2 shadow-6 sm:w-[min(720px,calc(100vw-2rem))] md:left-0 md:right-auto md:translate-x-0 md:max-h-none ${
              isPopupUp ? 'md:bottom-full md:top-auto md:mb-2' : ''
            }`}
          >
            <div className="date-picker-buttons flex flex-col gap-3 pb-2 sm:flex-row sm:justify-end sm:gap-4.5">
              <Button variant="secondary" type="submit" onClick={handleCancel}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleApply}>
                Apply
              </Button>
            </div>

            <DateRangePicker
              direction={pickerLayoutDirection}
              months={isMobile ? 1 : 2}
              ranges={[selectionRange]}
              className="relative date-picker-panel"
              staticRanges={staticRanges}
              onChange={handleSelect}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default DatePicker;
